"use client";

import { type ComponentType, type SVGProps, useEffect, useRef, useState } from "react";
import { BrowserWindow } from "@/components/browser-window";
import { CouponDiscountIcon, GlobeMinusIcon, PointClickIcon } from "@/components/icons/icons";
import { cn } from "@/utils";

const cards = [
	{
		title: "Add Products in Seconds",
		description: "Type, click, done – your products are live.",
		icon: PointClickIcon,
		video: {
			mp4: "/yns-video-1.mp4",
			webm: "/yns-video-1.webm",
			poster: "/yns-video-1.jpg",
		},
	},
	{
		title: " Build It Your Way",
		description: "Designing a store has never been this fun – or this fast.",
		icon: CouponDiscountIcon,
		video: {
			mp4: "/yns-video-2.mp4",
			webm: "/yns-video-2.webm",
			poster: "/yns-video-2.jpg",
		},
	},
	{
		title: "Launch Lightning-Fast",
		description: "Speed that makes your old store jealous.",
		icon: GlobeMinusIcon,
		video: {
			mp4: "/yns-video-3.mp4",
			webm: "/yns-video-3.webm",
			poster: "/yns-video-3.jpg",
		},
	},
];

export const FeatureVideos = () => {
	const [active, setActive] = useState(0);

	const videosRef = useRef<(HTMLVideoElement | null)[]>([]);

	useEffect(() => {
		if (videosRef.current[active]) {
			videosRef.current[active]?.play();
		}

		videosRef.current.forEach((video, index) => {
			if (video && index !== active) {
				video.pause();
			}
		});
	}, [active]);

	return (
		<section
			className="mt-10 px-4 pt-10 bg-gradient-to-b from-0% to-[1.5rem] from-[#fafafa] to-transparent"
			id="features"
		>
			<h2 className="sr-only">YNS most important features</h2>
			<div className="grid gap-12 relative">
				{cards.map((card, index) => (
					<FeatureCard
						key={index}
						title={card.title}
						description={card.description}
						icon={card.icon}
						align={index === 0 ? "left" : index === 1 ? "center" : "right"}
						className={cn(index === 0 ? "relative" : "md:absolute top-0 left-0 right-0")}
						active={active === index}
						onClick={() => setActive(index)}
						ref={(el) => {
							videosRef.current[index] = el;

							const fn = () => {
								setActive((index + 1) % cards.length);
							};
							el?.addEventListener("ended", fn);
							return () => {
								el?.removeEventListener("ended", fn);
							};
						}}
						video={card.video}
					/>
				))}
			</div>
		</section>
	);
};

const FeatureCard = ({
	title,
	description,
	icon: Icon,
	align,
	className,
	active,
	onClick,
	ref,
	video,
}: {
	title: string;
	description: string;
	icon: ComponentType<SVGProps<SVGSVGElement>>;
	align: "left" | "center" | "right";
	className?: string;
	active: boolean;
	onClick?: () => void;
	ref?: React.Ref<HTMLVideoElement>;
	video: {
		mp4: string;
		webm: string;
		poster: string;
	};
}) => {
	return (
		<article className={cn(className)}>
			<BrowserWindow className={cn(active && "z-30")} onClick={onClick}>
				<video
					controls={false}
					autoPlay={false}
					muted
					className="w-full h-full object-cover rounded-xs"
					ref={ref}
					poster={video.poster}
				>
					<source src={video.webm} type="video/webm" />
					<source src={video.mp4} type="video/mp4" />
				</video>
			</BrowserWindow>
			<div
				className={cn(
					"grid md:grid-cols-3",
					// "w-1/3",
					// {
					// 	"md:ml-[calc(0/3*100%)] relative z-10": align === "left",
					// 	"md:ml-[calc(1/3*100%)] relative z-10": align === "center",
					// 	"md:ml-[calc(2/3*100%)] relative z-10": align === "right",
					// },
				)}
			>
				<button
					type="button"
					aria-label={`Select video ${title}`}
					onClick={onClick}
					className={cn(
						"appearance-none text-left",
						"group relative grid grid-cols-[calc((100vw-1rem*2)/4)_1fr] pt-6 pr-4 bg-[linear-gradient(transparent_0%,#fafafad0_30%,#fafafad0_70%,transparent_100%)]",
						"md:grid-cols-[5rem_1fr] md:gap-4",
						{
							"md:col-start-1 relative z-10": align === "left",
							"md:col-start-2 relative z-10": align === "center",
							"md:col-start-3 relative z-10": align === "right",
						},
					)}
				>
					<svg
						viewBox="0 0 200 100"
						xmlns="http://www.w3.org/2000/svg"
						className={cn(
							"absolute opacity-0 group-hover:opacity-50 transition-opacity -top-1/2 left-1/2 -translate-x-1/2 w-[150px] fill-brand-500 blur-[50px]",
							"md:scale-150",
							active && "opacity-50",
						)}
					>
						<ellipse cx="100" cy="50" rx="100" ry="50" />
					</svg>
					<Icon className="justify-self-center text-neutral-400 group-hover:text-brand-500 transition-colors md:justify-self-end" />
					<div>
						<h3 className="text-h6 leading-h6 tracking-h6 font-medium group-hover:text-black transition-colors">
							{title}
						</h3>
						<p className="text-pregular leading-pregular tracking-pregular text-neutral-400 group-hover:text-pregular-500 transition-colors mt-1">
							{description}
						</p>
					</div>
				</button>
			</div>
		</article>
	);
};
